/**
 * The Activity page's labels and filters (firm-model spec §9, §10.2), pure so the rules are
 * tested without rendering.
 */
import type { ActivityAction, ActivityMember, ActivityQuery, ActivityRun } from "@contract/firm/activity";

const DATE = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export const whenLabel = (iso: string): string => DATE.format(new Date(iso));

/** A member as the page names them: their email (unique, where two members can share a
 * name; Dan 2026-10-02), else their name, else "A former member". */
export const memberText = (m: ActivityMember | undefined): string => m?.email ?? m?.name ?? "A former member";

/** A row's member, by id. */
export function memberLabel(members: ActivityMember[], userId: string): string {
  return memberText(members.find((x) => x.userId === userId));
}

export const skillLabel = (skillId: string | null): string => (skillId ? `/${skillId}` : "No skill");

export function scopeLabel(run: ActivityRun): string {
  if (run.scope === "deck") return "Deck";
  if (run.scope === "slide") return "Slide";
  return "";
}

export function findingsLabel(run: ActivityRun): string {
  if (run.findings === null) return "";
  return run.findings === 1 ? "1 finding" : `${run.findings} findings`;
}

/** Slides read of the slides in scope, and whether the run completed. */
export function coverageLabel(run: ActivityRun): string {
  const parts: string[] = [];
  if (run.coverage) parts.push(`${run.coverage.slidesRead} of ${run.coverage.slidesTotal} slides read`);
  if (run.ok === false) parts.push("did not finish");
  return parts.join(", ");
}

export function actionLabel(action: ActivityAction): string {
  if (action.action === "dismissed") return "Dismissed";
  if (action.proof === "verified") return "Fixed, verified by a re-check";
  if (action.proof === "user_marked") return "Marked fixed by the member";
  return "Fixed, not re-checked";
}

/** The filters the page holds; days are `YYYY-MM-DD` from a date input, "" = none. */
export type ActivityFilters = {
  kind: ActivityQuery["kind"];
  member: string | null;
  skill: string | null;
  fromDay: string;
  toDay: string;
  page: number;
};

export const NO_FILTERS: ActivityFilters = { kind: "runs", member: null, skill: null, fromDay: "", toDay: "", page: 1 };

/** Local midnight of a `YYYY-MM-DD` day, or null when it isn't one. */
function startOfDay(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** The server query: `from` is the start of the From day, `to` the start of the day after
 * the To day, so both days are included. */
export function toQuery(f: ActivityFilters): ActivityQuery {
  const q: ActivityQuery = { kind: f.kind, page: f.page };
  const from = startOfDay(f.fromDay);
  const to = startOfDay(f.toDay);
  if (from) q.from = from.toISOString();
  if (to) q.to = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1).toISOString();
  if (f.member) q.member = f.member;
  if (f.skill) q.skill = f.skill;
  return q;
}
